'use client';
import { ApiClientProvider as WebshopApiClientProvider } from '@cldsystems/ws-api-client';
import { HeroUIProvider } from '@heroui/react';
import dynamic from 'next/dynamic';
import { createContext, FC, PropsWithChildren, Suspense, useContext, useEffect } from 'react';
import MailerLiteSubscribeTracker from '@/components/common/mailerlite-subscribe-tracker';
import { PageVisitClearing } from '@/components/common/page-visit-clearing/page-visit-clearing';
import useNavigate from '@/hooks/use-navigate';
import { runtimeHttpClient } from '@/services/services.setup';
import { setupAxiosCorsErrorHandler } from '@/services/setup-cors-error-handler';
import { KeycloakProvider } from './keycloak-provider';
import QueryClientProvider from './query-client-provider';
import RoleGuardProvider from './role-guard-provider';
import { TooltipProvider } from './tooltip-provider';

const Toaster = dynamic(() => import('sonner').then(module => ({ default: module.Toaster })), {
  ssr: false,
});

const DeferredCorsErrorModal = dynamic(
  () => import('@/components/common/cors-error-modal/cors-error-modal'),
  { ssr: false },
);

/**
 * Provides the default locale and the supported locale codes (from `coreConfig`) to
 * client-side helpers such as `<Link>`, `useNavigate` and `useLocale`, so they can
 * produce localized URLs even on routes without a `[locale]` segment (e.g. the root
 * `/` landing page).
 *
 * Lives at the `RootProvider` level so it's available BEFORE `AppProvider`, which is
 * required because `RootProvider` itself calls `useNavigate` to wire up HeroUI (and
 * therefore cannot read `coreConfig` through `useCoreConfig`).
 */
const DefaultLocaleContext = createContext<string | undefined>(undefined);
const SupportedLocalesContext = createContext<string[] | undefined>(undefined);

export const useDefaultLocale = (): string | undefined => useContext(DefaultLocaleContext);

export const useSupportedLocales = (): string[] | undefined => useContext(SupportedLocalesContext);

type RootProviderProps = PropsWithChildren<{
  defaultLocale?: string;
  supportedLocales?: string[];
}>;

const RootProvider: FC<RootProviderProps> = ({ children, defaultLocale, supportedLocales }) => {
  return (
    <DefaultLocaleContext.Provider value={defaultLocale}>
      <SupportedLocalesContext.Provider value={supportedLocales}>
        <RootProviderInner>{children}</RootProviderInner>
      </SupportedLocalesContext.Provider>
    </DefaultLocaleContext.Provider>
  );
};

const RootProviderInner: FC<PropsWithChildren> = ({ children }) => {
  const navigate = useNavigate();

  useEffect(() => {
    setupAxiosCorsErrorHandler(runtimeHttpClient);
  }, []);

  return (
    <KeycloakProvider>
      <RoleGuardProvider>
        <QueryClientProvider>
          <WebshopApiClientProvider httpClient={runtimeHttpClient} hasSchema={false}>
            <DeferredCorsErrorModal />
            <PageVisitClearing />
            <Suspense fallback={null}>
              <MailerLiteSubscribeTracker />
            </Suspense>
            <Toaster position="bottom-right" closeButton richColors />
            <HeroUIProvider navigate={navigate}>
              <TooltipProvider>{children}</TooltipProvider>
            </HeroUIProvider>
          </WebshopApiClientProvider>
        </QueryClientProvider>
      </RoleGuardProvider>
    </KeycloakProvider>
  );
};

export default RootProvider;
